import Excel from 'exceljs';
import JSZip from 'jszip';

const SPREADSHEET_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';

const normalizeExcelBuffer = async (buffer: ArrayBuffer): Promise<ArrayBuffer> => {
  const zip = await JSZip.loadAsync(buffer);
  const parser = new DOMParser();
  let modified = false;

  for (const name of Object.keys(zip.files)) {
    if (
      name !== 'xl/workbook.xml' &&
      name !== 'xl/styles.xml' &&
      name !== 'xl/sharedStrings.xml' &&
      !/^xl\/worksheets\/sheet\d+\.xml$/.test(name)
    ) {
      continue;
    }

    const xml = await zip.files[name].async('string');
    const doc = parser.parseFromString(xml, 'application/xml');

    if (doc.getElementsByTagName('parsererror').length) continue;

    const root = doc.documentElement;

    if (
      root.namespaceURI !== SPREADSHEET_NS ||
      root.prefix !== 'x' ||
      root.getAttribute('xmlns:x') !== SPREADSHEET_NS ||
      root.hasAttribute('xmlns')
    ) {
      continue;
    }

    if (xml.includes('<![CDATA[') || xml.includes('<!--')) {
      continue;
    }

    const elements = Array.from(doc.getElementsByTagName('*'));

    if (elements.some(element => element.prefix === 'x' && element.namespaceURI !== SPREADSHEET_NS)) {
      continue;
    }

    const normalized = xml
      .replace(
        /xmlns:x=(["'])http:\/\/schemas\.openxmlformats\.org\/spreadsheetml\/2006\/main\1/,
        `xmlns="${SPREADSHEET_NS}" xmlns:x="${SPREADSHEET_NS}"`
      )
      .replace(/<(\/?)x:([A-Za-z_][\w.-]*)(?=[\s/>])/g, '<$1$2');

    if (normalized !== xml) {
      zip.file(name, normalized);
      modified = true;
    }
  }

  return modified ? zip.generateAsync({ type: 'arraybuffer' }) : buffer;
};

type ExcelFileResult = {
  file: File;
  workbook: Excel.Workbook;
};

const readExcelFile = async (file: File): Promise<ExcelFileResult | null> => {
  try {
    const originalBuffer = await file.arrayBuffer();
    const workbook = new Excel.Workbook();

    try {
      await workbook.xlsx.load(originalBuffer);

      if (workbook.worksheets.length > 0) {
        return { file, workbook };
      }

      return null;
    } catch {}

    const normalizedBuffer = await normalizeExcelBuffer(originalBuffer);

    if (normalizedBuffer === originalBuffer) {
      return null;
    }

    const normalizedWorkbook = new Excel.Workbook();
    await normalizedWorkbook.xlsx.load(normalizedBuffer);

    if (normalizedWorkbook.worksheets.length === 0) {
      return null;
    }

    const normalizedFile = new File([normalizedBuffer], file.name, {
      type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      lastModified: file.lastModified,
    });

    return {
      file: normalizedFile,
      workbook: normalizedWorkbook,
    };
  } catch {
    return null;
  }
};

export const prepareExcelFile = async (file: File): Promise<File | null> => {
  const result = await readExcelFile(file);
  return result?.file ?? null;
};

export const loadExcelFile = async (file: File): Promise<Excel.Workbook | null> => {
  const result = await readExcelFile(file);
  return result?.workbook ?? null;
};
